import BackIcon from '@/common/components/icons/back';
import { SETTINGS_PAGE_BG } from '@/common/components/settings/settingsList';
import { SkeletonBubbles } from '../app/components/MessagesSkeleton';
import ChatsListSkeleton from '../app/components/skeleton/ChatsListSkeleton';
import ArchiveIcon from '../app/icons/ArchiveIcon';
import { useRouter } from 'next/router';
import PillTabs from '../app/components/PillTabs';

/**
 * The messenger's own first frame, shown from the moment /_/chats opens until the real list is
 * on screen — the same header (title + search), chips, archive row and the list's own skeleton
 * rows, so the page never jumps between two different skeletons or flashes white.
 */
const SKEL = 'block animate-pulse bg-slate-200';
const SKELETON_TABS = [
  { id: 'all', label: 'همه' },
  { id: 'unread', label: 'خوانده‌نشده' },
];

/**
 * The archive row depends on what this device saved (TicketsSidebar's readArchivedCounts), which the
 * server cannot know. So the server always sends the row hidden and this flag on <html>, set before
 * the first paint and outside React, shows it through CSS: no hydration mismatch.
 */
const MARK_SAVED_ARCHIVE = `try{var v=JSON.parse(localStorage.getItem("hami_archived_counts")||"null");if(Number(v&&v.count)>0&&localStorage.getItem("hami_beta_archive")!=="0")document.documentElement.setAttribute("data-hami-archived","")}catch(e){}`;

// Arriving from another page of the site there is no server HTML, so the inline copy below never ran.
if (typeof document !== 'undefined') {
  const script = document.createElement('script');
  script.text = MARK_SAVED_ARCHIVE;
  document.head.appendChild(script).remove();
}

export const NativeChatsSkeleton = ({ chatDetail = false, archive = false }: { chatDetail?: boolean; archive?: boolean }) =>
  chatDetail ? (
    <div className="absolute inset-0 z-10 flex h-full w-full flex-col bg-[#f2f2f2]" role="status" aria-label="در حال بارگذاری گفت‌وگو" dir="rtl">
      <div className="flex items-center gap-1.5 px-1.5 pb-1 pt-[calc(5px+env(safe-area-inset-top,0px))]">
        <span className="block size-[46px] shrink-0 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.12)]" />
        <span className="flex h-[46px] flex-1 items-center gap-2 rounded-full bg-white ps-0.5 shadow-[0_1px_3px_rgba(0,0,0,0.12)]">
          <span className={`${SKEL} size-[42px] rounded-full`} />
          <span className={`${SKEL} h-3 w-1/3 rounded-md`} />
        </span>
        <span className="block h-[46px] w-[88px] shrink-0 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.12)]" />
      </div>
      <div className="flex min-h-0 flex-1 flex-col justify-end gap-2 overflow-hidden px-2.5 pb-4">
        <SkeletonBubbles />
      </div>
      {/* the composer's own box: white 50px pill with the blue mic at its end */}
      <div className="px-2 pb-[max(0.875rem,calc(env(safe-area-inset-bottom)+0.375rem))] pt-1">
        <span className="flex h-[50px] items-center justify-end rounded-[25px] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.12)]">
          <span className="m-[3px] block size-11 rounded-full bg-primary" />
        </span>
      </div>
    </div>
  ) : (
    <div className="absolute inset-0 z-10 flex h-full w-full flex-col overflow-hidden bg-white" role="status" aria-label="در حال بارگذاری گفت‌وگوها" dir="rtl">
      <script dangerouslySetInnerHTML={{ __html: MARK_SAVED_ARCHIVE }} />
      {archive ? (
        <div className="flex h-14 items-center gap-2 px-2.5 pt-[env(safe-area-inset-top,0px)] [box-sizing:content-box]">
          <span className="-ms-2.5 flex size-12 shrink-0 items-center justify-center text-slate-700">
            <BackIcon className="h-6 w-6" />
          </span>
          <span className="text-base font-medium text-slate-900">بایگانی‌شده‌ها</span>
        </div>
      ) : (
        <div className="px-3 pt-[env(safe-area-inset-top,0px)]">
          <div className="flex h-14 items-center justify-between gap-2">
            <span className="text-[20px] font-semibold text-slate-900">گفت‌وگوها</span>
            <span className={`${SKEL} -me-1.5 size-10 rounded-full !bg-slate-100`} />
          </div>
          <PillTabs className="mb-2 mt-0.5" label="فیلتر گفت‌وگوها" value="all" onChange={undefined} items={SKELETON_TABS} />
        </div>
      )}
      {/* the list's own top padding: its 4px under the header */}
      <div className="pt-1" />
      {!archive && (
        <div className="hidden w-full shrink-0 items-center gap-3 border-0 border-b border-solid border-slate-100 bg-white px-4 py-2.5 text-[15px] text-slate-900 [html[data-hami-archived]_&]:flex">
          <span className="flex size-12 flex-none items-center justify-center rounded-full bg-slate-100 text-slate-500">
            <ArchiveIcon />
          </span>
          <span className="font-medium">بایگانی‌شده‌ها</span>
        </div>
      )}
      <ChatsListSkeleton count={8} animate={false} />
    </div>
  );

/**
 * First frame of the other messenger screens (profile, settings, stories, health record): their own
 * grey page, empty — never the chat list's rows, which used to flash before e.g. the profile.
 */
export const NativeScreenSkeleton = () => (
  <div className={`absolute inset-0 z-10 h-full w-full ${SETTINGS_PAGE_BG}`} role="status" aria-label="در حال بارگذاری" dir="rtl" />
);

/** Which first frame a messenger URL gets. */
export const skeletonKindOfAsPath = (asPath: string): 'list' | 'chat' | 'screen' => {
  const path = asPath.split(/[?#]/)[0];
  if (/^\/_\/chats\/?$/.test(path)) return 'list';
  if (isChatDetailAsPath(path)) return 'chat';
  return 'screen';
};

/** The skeleton for the current URL — from the router, so the server and the first client render agree. */
export const RouteChatsSkeleton = () => {
  const { asPath } = useRouter();
  const kind = skeletonKindOfAsPath(asPath);
  if (kind === 'screen') return <NativeScreenSkeleton />;
  return <NativeChatsSkeleton chatDetail={kind === 'chat'} archive={kind === 'list' && /[?&]archive=1(&|#|$)/.test(asPath)} />;
};

/** «/_/chats/12/» is a chat; the list and «/_/chats/settings/…» are not. */
export const isChatDetailAsPath = (asPath: string) => /^\/_\/chats\/(?!settings(\/|$))[^/?#]+/.test(asPath);
